import { ExtendedDocument, ProductNote } from '../../../types/DocumentsTypes/Document';
import { deserializeDocument } from '../../../types/DocumentsTypes/utils';
import { createOrderData, OrderData, writeOrdersToOD } from '../../../types/OrderData';
import { dateObjectToString, roundDecimals } from '../../../fromKotlin/nkutils';
import { subStringMatch } from '../../../utils/sort';

/**
 * Token-aware "contains" search (same behaviour as the orders datasheet autocomplete):
 * every whitespace-separated token in the query must be a substring of at least one of
 * the candidate fields. Numbers are matched as plain strings so "5" matches "555"/"525"
 * while staying meaningful, and ordering is handled separately by {@link numericAwareCompare}.
 */
export const matchesTouchQuery = (query: string, fields: (string | number | undefined | null)[]): boolean => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const parts = fields.filter((f) => f !== undefined && f !== null && `${f}` !== '').map((f) => `${f}`.toLowerCase());
    return subStringMatch(q, parts);
};

/** Compares two ids numerically when both look like numbers, otherwise lexicographically. */
export const numericAwareCompare = (a: string | number | undefined, b: string | number | undefined): number => {
    const na = Number(a);
    const nb = Number(b);
    const aNum = a !== undefined && a !== null && `${a}` !== '' && !Number.isNaN(na);
    const bNum = b !== undefined && b !== null && `${b}` !== '' && !Number.isNaN(nb);
    if (aNum && bNum) return na - nb;
    if (aNum) return -1;
    if (bNum) return 1;
    return String(a ?? '').localeCompare(String(b ?? ''));
};

export type ProductSortMode = 'priority' | 'name' | 'id';

export interface ProductLine {
    id: number;
    name: string;
    unit: string;
    qty: number;
    position: number;
    isSecondUnit: boolean;
}

export interface SummationClientLine {
    id: string;
    name: string;
    qty: number;
    orders: number;
}

export interface SummationLine {
    id: number;
    name: string;
    unit: string;
    total: number;
    orders: number;
    barcode?: string;
    externalId?: string;
    clients: SummationClientLine[];
}

const addToSummation = (
    map: Record<number, SummationLine>,
    p: { id: number; name?: string; unit?: string; available_units?: string[]; barcode?: string; external_id?: string },
    qty: number,
    clientId: string | number | undefined,
    clientName: string | undefined
) => {
    if (!qty) return;
    if (!map[p.id]) {
        map[p.id] = {
            id: p.id,
            name: p.name ?? `מוצר ${p.id}`,
            unit: p.available_units?.[0] ?? p.unit ?? '',
            total: 0,
            orders: 0,
            barcode: p.barcode || undefined,
            externalId: p.external_id || undefined,
            clients: []
        };
    }
    const line = map[p.id];
    line.total += qty;
    line.orders += 1;
    if (!line.barcode && p.barcode) line.barcode = p.barcode;
    if (!line.externalId && p.external_id) line.externalId = p.external_id;
    const cid = String(clientId ?? '');
    if (!cid || cid === '0') return;
    let c = line.clients.find((x) => x.id === cid);
    if (!c) {
        c = { id: cid, name: clientName || `לקוח ${cid}`, qty: 0, orders: 0 };
        line.clients.push(c);
    }
    c.qty += qty;
    c.orders += 1;
};

const finalizeSummation = (map: Record<number, SummationLine>): SummationLine[] =>
    Object.values(map)
        .map((s) => ({
            ...s,
            total: roundDecimals(s.total),
            clients: s.clients
                .map((c) => ({ ...c, qty: roundDecimals(c.qty) }))
                .sort((a, b) => b.qty - a.qty || a.name.localeCompare(b.name))
        }))
        .sort((a, b) => b.total - a.total);

/**
 * Builds the canonical OrderData row (with p_{id} amount fields) for a single order
 * document, reusing the exact same serializer used by the full orders grid so that
 * saving an order / issuing a delivery note stays consistent with the rest of the system.
 */
export const buildOrderData = (order: ProductNote, date: Date): OrderData => {
    const on = createOrderData({
        orderId: String(order.id),
        id: String(order.entId),
        customer: order.entName ?? '',
        readonly: false,
        date: dateObjectToString(date)
    });
    on.externalId = (order as any).externalId;
    try {
        writeOrdersToOD(on, order, true);
    } catch (e) {
        // The heavy serializer can throw on partial catalogs; the amount fields it
        // already wrote are still usable, so we keep going with what we have.
        // eslint-disable-next-line no-console
        console.error('buildOrderData writeOrdersToOD failed', e);
    }
    return on;
};

const P_KEY = /^p_(\d+)$/;

/** Extracts editable product lines from a built OrderData row. */
export const getProductLines = (on: OrderData, order: ProductNote): ProductLine[] => {
    const byId: Record<number, any> = {};
    (order.products ?? []).forEach((p) => {
        byId[p.id] = p;
    });
    const lines: ProductLine[] = [];
    Object.keys(on).forEach((k) => {
        const m = k.match(P_KEY);
        if (!m) return;
        const id = Number(m[1]);
        const p = byId[id] ?? {};
        const isSecondUnit = Boolean((on as any)[`u_${id}`]);
        const units: string[] = p.available_units ?? [];
        const unit = (isSecondUnit ? units[1] : units[0]) ?? p.unit ?? '';
        lines.push({
            id,
            name: p.name ?? `מוצר ${id}`,
            unit,
            qty: (on as any)[k] ?? 0,
            position: p.position ?? 0,
            isSecondUnit
        });
    });
    lines.sort((a, b) => a.position - b.position || a.id - b.id);
    return lines;
};

/** Applies edited quantities back onto the OrderData row (removing zeroed lines). */
export const applyLinesToOrderData = (on: OrderData, lines: ProductLine[]): OrderData => {
    lines.forEach((l) => {
        const key = `p_${l.id}`;
        if (!l.qty || l.qty <= 0) {
            delete (on as any)[key];
        } else {
            (on as any)[key] = l.qty;
        }
    });
    return on;
};

/** Normalised "to produce" quantity for an order product (main units). */
export const productMainQty = (p: any): number => {
    const main = p.quantity ?? 0;
    const second = p.quantitySecond ?? 0;
    const ratio = p.conversion_ratio ?? 0;
    if (main === 0 && second > 0 && ratio > 0) return second * ratio;
    return main + second * ratio;
};

/** Normalised returns quantity for a product (main units). */
export const productMainReturns = (p: any): number => {
    const main = p.quantityReturns ?? 0;
    const second = p.quantitySecondReturns ?? 0;
    const ratio = p.conversion_ratio ?? 0;
    if (main === 0 && second > 0 && ratio > 0) return second * ratio;
    return main + second * ratio;
};

/** Normalised net quantity on a delivery note line (delivered minus returns). */
export const productNetQty = (p: any): number => productMainQty(p) - productMainReturns(p);

/**
 * Resolves a delivery note for summation: active client delivery certificates only,
 * preferring the already-deserialized document from the API and falling back to nkObject.
 */
export const toSummationDeliveryNote = (d: ExtendedDocument): ProductNote | null => {
    if (!d || d.status === 'canceled' || d.type !== 'delivery_certificate') return null;

    const withProducts = d as ProductNote;
    if (withProducts.products?.length) {
        return withProducts;
    }
    if (!d.nkObject) return null;
    try {
        const doc = deserializeDocument(d.nkObject) as ProductNote;
        return doc?.type === 'delivery_certificate' && doc.status !== 'canceled' ? doc : null;
    } catch {
        return null;
    }
};

/** Aggregates products across issued delivery notes for the daily production summary. */
export const buildDeliverySummation = (dvs: ExtendedDocument[]): SummationLine[] => {
    const map: Record<number, SummationLine> = {};
    dvs.forEach((d) => {
        const note = toSummationDeliveryNote(d);
        if (!note) return;
        (note.products ?? []).forEach((p) => {
            addToSummation(map, p, productNetQty(p), note.entId, note.entName);
        });
    });
    return finalizeSummation(map);
};

/** Aggregates every product across the given orders for the daily production summary. */
export const buildSummation = (orders: ProductNote[]): SummationLine[] => {
    const map: Record<number, SummationLine> = {};
    orders.forEach((o) => {
        (o.products ?? []).forEach((p) => {
            addToSummation(map, p, productMainQty(p), o.entId, o.entName);
        });
    });
    return finalizeSummation(map);
};

/** Count of distinct products in an order (with a non-zero amount). */
export const orderProductCount = (o: ProductNote): number =>
    (o.products ?? []).filter((p) => productMainQty(p) !== 0).length;

/** Count of non-zero product lines on a note, counting both delivered and returned goods. */
export const noteProductCount = (o: ProductNote): number =>
    (o.products ?? []).filter((p) => productMainQty(p) !== 0 || productMainReturns(p) !== 0).length;

/** Delivery notes belonging to a given order, matched by their orderId back-reference. */
export const dvsForOrder = (order: ProductNote, dvs: ExtendedDocument[]): ExtendedDocument[] =>
    dvs.filter((d) => Number((d as any).orderId) === Number(order.id) && d.status !== 'canceled');

/**
 * Guards against a stale/empty client id reaching the server as ent_id=0. This can happen if
 * a tap lands on the touch dialog while it's fading out after `onClose()` (e.g. right after
 * canceling a delivery note, when the footer re-renders into the "create" buttons). Applied
 * on the touch screen only, right before building/sending an OrderData row — never inside the
 * shared `newAllOrderNote` API, since other screens (e.g. the datasheet) legitimately use
 * non-positive sentinel values for `orderId` (not the client id) on unsaved rows.
 */
export const hasValidClientId = (clientId: string | number | undefined | null): boolean => Number(clientId) > 0;

/** Stable unique key for a touch-list row (same product id may appear more than once). */
let catalogRowUidSeq = 0;
export const newCatalogRowUid = (): string => {
    catalogRowUidSeq += 1;
    return `cr_${Date.now()}_${catalogRowUidSeq}`;
};

/** A single editable catalog row: the client's product + its (client) price + chosen amount. */
export interface CatalogRow {
    id: number;
    name: string;
    unit: string;
    category: string;
    position: number;
    price: number;
    qty: number;
    /** Unique among rows in the list; needed when the same product has 2+ lines. */
    rowUid: string;
    /**
     * Extra line of a product that already has a primary catalog row (user-duplicated, or a
     * second+ line loaded from an existing delivery note). Removable independently.
     */
    isExtra?: boolean;
    externalId?: string;
    barcode?: string;
    /** Product exists on the order/note but is not in the client's live price catalog. */
    missingFromCatalog?: boolean;
}

/** Clones a row as an extra line of the same product (qty starts at 0). */
export const duplicateCatalogRow = (row: CatalogRow): CatalogRow => ({
    ...row,
    rowUid: newCatalogRowUid(),
    qty: 0,
    isExtra: true
});

/**
 * Merges the client's full priced catalog with the amounts of an existing order (if any),
 * producing the touch list of rows. Products present in the order but missing from the live
 * catalog are appended so nothing is silently dropped.
 */
export const buildCatalogRows = (catalog: any[], order: ProductNote | null, date: Date): CatalogRow[] => {
    const existing = order ? buildOrderData(order, date) : null;
    const rows: CatalogRow[] = [];
    const seen = new Set<number>();
    (catalog ?? []).forEach((c) => {
        seen.add(c.id);
        rows.push({
            id: c.id,
            name: c.name ?? `מוצר ${c.id}`,
            unit: (c.available_units?.[0]) ?? c.unit ?? '',
            category: c.category ?? '',
            position: c.position ?? 0,
            price: c.totalPrice ?? 0,
            qty: existing ? Number((existing as any)[`p_${c.id}`] ?? 0) : 0,
            rowUid: newCatalogRowUid(),
            externalId: c.external_id ?? undefined,
            barcode: c.barcode ?? undefined
        });
    });
    if (existing) {
        Object.keys(existing).forEach((k) => {
            const m = k.match(/^p_(\d+)$/);
            if (!m) return;
            const id = Number(m[1]);
            if (seen.has(id)) return;
            const op = (order?.products ?? []).find((p) => p.id === id);
            rows.push({
                id,
                name: op?.name ?? `מוצר ${id}`,
                unit: (op?.available_units?.[0]) ?? op?.unit ?? '',
                category: op?.category ?? '',
                position: op?.position ?? 9999,
                price: Number((existing as any)[`price_${id}`] ?? op?.totalPrice ?? 0),
                qty: Number((existing as any)[k] ?? 0),
                rowUid: newCatalogRowUid(),
                externalId: op?.external_id ?? undefined,
                barcode: op?.barcode ?? undefined,
                missingFromCatalog: true
            });
        });
    }
    rows.sort((a, b) => a.position - b.position || a.id - b.id);
    return rows;
};

/**
 * Builds an OrderData row from the edited catalog rows, preserving the existing order's
 * meta (notes/status/ref) when editing, and writing amount + client price per product.
 */
export const buildOrderDataFromRows = (
    rows: CatalogRow[],
    opts: {
        clientId: string;
        clientName: string;
        orderId?: string;
        date: Date;
        order?: ProductNote | null;
        /** When true the amounts are written as returns (see deserializeOrderDataToOrder). */
        returns?: boolean;
    }
): OrderData => {
    const base: any = opts.order
        ? buildOrderData(opts.order, opts.date)
        : createOrderData({
              id: opts.clientId,
              orderId: opts.orderId ?? '',
              customer: opts.clientName,
              readonly: false,
              date: dateObjectToString(opts.date)
          });
    // OrderData is keyed by product id — sum quantities when the same product has 2+ rows.
    const touched = new Set<number>();
    rows.forEach((r) => touched.add(r.id));
    touched.forEach((id) => {
        delete base[`p_${id}`];
    });
    rows.forEach((r) => {
        if (!(r.qty && r.qty > 0)) return;
        const k = `p_${r.id}`;
        base[k] = roundDecimals((Number(base[k]) || 0) + r.qty);
        if (r.price > 0) base[`price_${r.id}`] = r.price;
    });
    // Flag consumed by deserializeOrderDataToOrder to write the amounts as returns.
    if (opts.returns) base.returnsOnly = true;
    return base as OrderData;
};

/** Builds editable rows directly from an issued note/DV (so its amounts can be edited & reprinted). */
export const buildRowsFromNote = (note: ProductNote, useReturns = false): CatalogRow[] =>
    (note.products ?? [])
        .map((p: any, index: number) => ({
            id: p.id,
            name: p.name ?? `מוצר ${p.id}`,
            unit: (p.available_units?.[0]) ?? p.unit ?? '',
            category: p.category ?? '',
            position: p.position ?? 0,
            price: p.totalPrice ?? 0,
            qty: useReturns ? productMainReturns(p) : productMainQty(p),
            rowUid: newCatalogRowUid(),
            // First occurrence of each product id is primary; later ones are extra lines.
            isExtra: (note.products ?? []).findIndex((x: any) => x.id === p.id) !== index,
            externalId: p.external_id ?? undefined,
            barcode: p.barcode ?? undefined
        }))
        .sort((a, b) => a.position - b.position || a.id - b.id);

/**
 * Builds editable rows for editing an issued note: the client's FULL priced catalog
 * (in price-priority order) with the note's amounts merged in, so products can be added
 * as well as changed. Note products missing from the live catalog are appended.
 * When the note has 2+ lines of the same product, each line becomes its own row.
 */
export const buildCatalogRowsForNote = (catalog: any[], note: ProductNote, useReturns = false): CatalogRow[] => {
    const noteLines = (note.products ?? []).map((p: any) => ({
        p,
        qty: useReturns ? productMainReturns(p) : productMainQty(p),
        price: p.totalPrice ?? 0
    }));
    const firstQtyById: Record<number, number> = {};
    const firstPriceById: Record<number, number> = {};
    const extrasById: Record<number, { qty: number; price: number; p: any }[]> = {};
    noteLines.forEach(({ p, qty, price }) => {
        if (firstQtyById[p.id] === undefined) {
            firstQtyById[p.id] = qty;
            if (price) firstPriceById[p.id] = price;
        } else {
            if (!extrasById[p.id]) extrasById[p.id] = [];
            extrasById[p.id].push({ qty, price, p });
        }
    });
    const rows: CatalogRow[] = [];
    const seen = new Set<number>();
    (catalog ?? []).forEach((c) => {
        seen.add(c.id);
        rows.push({
            id: c.id,
            name: c.name ?? `מוצר ${c.id}`,
            unit: (c.available_units?.[0]) ?? c.unit ?? '',
            category: c.category ?? '',
            position: c.position ?? 0,
            price: c.totalPrice ?? firstPriceById[c.id] ?? 0,
            qty: firstQtyById[c.id] ?? 0,
            rowUid: newCatalogRowUid(),
            externalId: c.external_id ?? undefined,
            barcode: c.barcode ?? undefined
        });
        (extrasById[c.id] ?? []).forEach((extra) => {
            rows.push({
                id: c.id,
                name: c.name ?? extra.p.name ?? `מוצר ${c.id}`,
                unit: (c.available_units?.[0]) ?? c.unit ?? '',
                category: c.category ?? '',
                position: c.position ?? 0,
                price: c.totalPrice ?? extra.price ?? 0,
                qty: extra.qty,
                rowUid: newCatalogRowUid(),
                isExtra: true,
                externalId: c.external_id ?? undefined,
                barcode: c.barcode ?? undefined
            });
        });
    });
    noteLines.forEach(({ p, qty, price }) => {
        if (seen.has(p.id)) return;
        // First missing-from-catalog occurrence is primary; later ones are extras.
        const already = rows.some((r) => r.id === p.id);
        rows.push({
            id: p.id,
            name: p.name ?? `מוצר ${p.id}`,
            unit: (p.available_units?.[0]) ?? p.unit ?? '',
            category: p.category ?? '',
            position: p.position ?? 9999,
            price: price || firstPriceById[p.id] || 0,
            qty,
            rowUid: newCatalogRowUid(),
            isExtra: already,
            externalId: p.external_id ?? undefined,
            barcode: p.barcode ?? undefined,
            missingFromCatalog: true
        });
    });
    rows.sort((a, b) => a.position - b.position || a.id - b.id || (a.isExtra ? 1 : 0) - (b.isExtra ? 1 : 0));
    return rows;
};

/**
 * Rebuilds a note's product list from edited rows (only rows with an amount survive),
 * preserving the original product line's metadata (price/discount/notes) when present and
 * creating a minimal line for newly-added products. Written for {@link update_note_amounts}.
 * Same product id may appear on multiple rows — each becomes its own note line.
 */
export const buildNoteProductsFromRows = (note: ProductNote, rows: CatalogRow[], useReturns = false): any[] => {
    const origPool: Record<number, any[]> = {};
    (note.products ?? []).forEach((p: any) => {
        if (!origPool[p.id]) origPool[p.id] = [];
        origPool[p.id].push(p);
    });
    return rows
        .filter((r) => r.qty > 0)
        .map((r, index) => {
            const pool = origPool[r.id] ?? [];
            const o = pool.shift();
            if (o)
                return useReturns
                    ? { ...o, quantityReturns: r.qty, quantitySecondReturns: 0, notePlacement: index }
                    : { ...o, quantity: r.qty, quantitySecond: 0, notePlacement: index };
            return {
                id: r.id,
                name: r.name,
                quantity: useReturns ? 0 : r.qty,
                quantityReturns: useReturns ? r.qty : 0,
                quantitySecond: 0,
                totalPrice: r.price,
                discount: 0,
                notePlacement: index
            };
        });
};

/** True when more than one chosen row shares the same product id. */
export const hasDuplicateProductLines = (rows: CatalogRow[]): boolean => {
    const seen = new Set<number>();
    for (const r of rows) {
        if (!(r.qty > 0)) continue;
        if (seen.has(r.id)) return true;
        seen.add(r.id);
    }
    return false;
};

/**
 * Sorts catalog rows for the touch list. The default is by the client's price priority
 * (position). When editing an existing order, rows that already carry an amount are kept
 * on top in their original (position) order, then everything else follows the chosen mode.
 */
export const sortCatalogRows = (
    rows: CatalogRow[],
    mode: ProductSortMode,
    opts?: { chosenOnTop?: boolean; preferExternalId?: boolean }
): CatalogRow[] => {
    const preferExternalId = opts?.preferExternalId ?? false;
    const idKey = (r: CatalogRow) =>
        preferExternalId && r.externalId !== undefined && `${r.externalId}` !== '' ? r.externalId : r.id;
    const byMode = (a: CatalogRow, b: CatalogRow): number => {
        if (mode === 'name') return a.name.localeCompare(b.name) || a.position - b.position;
        if (mode === 'id') return numericAwareCompare(idKey(a), idKey(b)) || a.position - b.position;
        // priority (default)
        return a.position - b.position || a.id - b.id;
    };
    const sorted = rows.slice().sort(byMode);
    if (!opts?.chosenOnTop) return sorted;
    const chosen = sorted.filter((r) => r.qty > 0).sort((a, b) => a.position - b.position || a.id - b.id);
    const rest = sorted.filter((r) => r.qty <= 0);
    return [...chosen, ...rest];
};

export interface ComparisonDayCol {
    key: string;
    label: string;
    weekday: string;
}

export const COMPARISON_NUM_DAYS = 7;

/** Build comparison columns ending at `endDate`, newest first. Skips Saturdays when requested. */
export const buildComparisonDays = (
    endDate: Date,
    count: number = COMPARISON_NUM_DAYS,
    includeSaturday = true
): ComparisonDayCol[] => {
    const arr: ComparisonDayCol[] = [];
    const d = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    let steps = 0;
    const maxSteps = count + (includeSaturday ? 0 : count) + 7;
    while (arr.length < count && steps < maxSteps) {
        if (includeSaturday || d.getDay() !== 6) {
            arr.push({
                key: dateObjectToString(d),
                label: d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit' }),
                weekday: d.toLocaleDateString('he-IL', { weekday: 'short' })
            });
        }
        d.setDate(d.getDate() - 1);
        steps += 1;
    }
    return arr;
};

/** Oldest calendar day covered by a comparison column list (newest-first). */
export const comparisonFromDate = (days: ComparisonDayCol[], fallback: Date): Date => {
    const oldest = days[days.length - 1];
    if (!oldest) return fallback;
    const [y, m, day] = oldest.key.split('-').map(Number);
    return new Date(y, m - 1, day);
};
